"use client";

import { useEffect, useState } from "react";

import {
  STRICT_CONSENT_REGION_CODES,
  updateGoogleConsent,
} from "@/lib/analytics";

import { Z2VAnalytics } from "./z2v-analytics";

const CONSENT_STORAGE_KEY = "simpleble_consent_v1";

type ConsentChoice = "denied" | "granted";

const strictRegions = new Set<string>(STRICT_CONSENT_REGION_CODES);

const getLocaleRegion = (locale: string): string | null => {
  const parts = locale.split("-");
  return parts.length > 1 ? (parts.at(-1)?.toUpperCase() ?? null) : null;
};

const isLikelyStrictConsentRegion = (): boolean => {
  const localeRegions = navigator.languages
    .map(getLocaleRegion)
    .filter((region): region is string => Boolean(region));
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return (
    localeRegions.some((region) => strictRegions.has(region)) ||
    timeZone.startsWith("Europe/") ||
    timeZone === "Atlantic/Reykjavik"
  );
};

export const ConsentBanner = (): React.ReactElement | null => {
  const [shouldShow, setShouldShow] = useState(false);
  const [choice, setChoice] = useState<ConsentChoice | null>(null);

  useEffect(() => {
    const stored = window.localStorage.getItem(
      CONSENT_STORAGE_KEY,
    ) as ConsentChoice | null;

    if (stored) {
      updateGoogleConsent(stored);
    }

    const timeoutId = window.setTimeout(() => {
      if (stored) {
        setChoice(stored);
      } else if (isLikelyStrictConsentRegion()) {
        setShouldShow(true);
      } else {
        setChoice("granted");
      }
    }, 0);

    return () => window.clearTimeout(timeoutId);
  }, []);

  const chooseConsent = (choice: ConsentChoice): void => {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, choice);
    updateGoogleConsent(choice);
    setChoice(choice);
    setShouldShow(false);
  };

  return (
    <>
      {choice === "granted" && <Z2VAnalytics />}
      {shouldShow && (
        <div className="border-primary/10 fixed right-3 bottom-3 left-3 z-[100] mx-auto flex max-w-[640px] flex-col gap-3 rounded-md border bg-white/95 p-3 shadow-sm md:right-5 md:bottom-5 md:left-auto md:w-[460px]">
          <p className="font-sora text-secondary text-xs leading-relaxed">
            Analytics preferences: help us understand how people shop the
            SimpleBLE Store, or continue without non-essential measurement.
          </p>
          <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={() => chooseConsent("denied")}
              className="font-sora border-primary/20 text-primary hover:bg-primary/5 min-h-10 rounded-md border px-3 py-2 text-xs font-semibold transition-colors"
            >
              Decline
            </button>
            <button
              type="button"
              onClick={() => chooseConsent("granted")}
              className="font-sora border-primary/20 text-primary hover:bg-primary/5 min-h-10 rounded-md border px-3 py-2 text-xs font-semibold transition-colors"
            >
              Accept
            </button>
          </div>
        </div>
      )}
    </>
  );
};
